// Libraries
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

// Supermove
import {DropdownInput as BaseDropdownInput, Icon, Space, Styled} from '@supermove/components';
import {ResponsiveType, useMountEffect, useResponsive, useSheet} from '@supermove/hooks';
import {colors} from '@supermove/styles';

// App
import DropdownSheet from '@shared/design/components/DropdownInput/components/DropdownSheet';
import Text from '@shared/design/components/Text';

export type DropdownOptionType = {
  label: string;
  value?: string | number | null;
  description?: string;
};

const Button = Styled.ButtonV2`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const DropdownIndicatorComponent = ({
  responsive,
  isDisabled,
}: {
  responsive: ResponsiveType;
  isDisabled?: boolean;
}) => {
  return (
    <Icon
      source={Icon.ChevronDown}
      size={responsive.desktop ? 14 : 16}
      color={isDisabled ? colors.gray.disabled : colors.gray.secondary}
      style={{marginLeft: 4, marginRight: 4, marginTop: 2}}
    />
  );
};

const ColorOption = ({isSelected, value, label}: any) => {
  return (
    <Row>
      <Icon source={Icon.Circle} size={8} color={value} />
      <Space width={8} />
      <Text.Body style={{color: isSelected ? colors.white : colors.black}}>{label}</Text.Body>
    </Row>
  );
};

const getSelectedSectionedOption = ({value, options}: {value: unknown; options: any[]}) => {
  const optionSection = _.find(options, (section) =>
    _.find(_.find(section.options, (option) => option.value === value) || ''),
  );
  return optionSection ? _.find(optionSection.options, (option) => option.value === value) : '';
};

const getFilterOption =
  ({additionalSearchKeys = []}: {additionalSearchKeys?: string[]}) =>
  (option: any, searchInput: string) => {
    // Support any option taking an isHidden prop to hide it from the dropdown.
    // Hidden options are not searchable but still show up in the input if it
    // is the selected value.
    if (option.data.isHidden) {
      return false;
    }

    // When searching, match the label or additional search keys
    const searchString = `${option.label}${additionalSearchKeys.reduce(
      (acc: string, key: string) => `${acc} ${_.get(option.data, key, '')}`,
      '',
    )}`;
    return searchString.toLowerCase().includes(searchInput.toLowerCase());
  };

const getHasMoreThanFourOptions = ({
  options,
  isSectionedList,
}: {
  options: any[];
  isSectionedList: boolean;
}) => {
  if (isSectionedList) {
    const allOptions = _.flatMap(options, (section) => section.options);
    return _.size(allOptions) > 4;
  }
  return _.size(options) > 4;
};

const getCustomComponents = ({isColorOptions}: {isColorOptions: boolean}) => {
  if (isColorOptions) {
    return {
      SingleValue: (props: any) => {
        return (
          <BaseDropdownInput.SingleValueContainer {...props}>
            <ColorOption value={props.data.value} label={props.data.label} />
          </BaseDropdownInput.SingleValueContainer>
        );
      },
      Option: (props: any) => {
        return (
          <BaseDropdownInput.OptionContainer {...props}>
            <ColorOption
              isSelected={props.isSelected}
              value={props.data.value}
              label={props.data.label}
            />
          </BaseDropdownInput.OptionContainer>
        );
      },
    };
  }
  return {};
};

const SheetButtonWrapper = ({
  responsive,
  mobileSheet,
  isDisabled,
  isResponsiveSheet,
  children,
}: {
  responsive: ResponsiveType;
  mobileSheet: any;
  isDisabled: boolean;
  isResponsiveSheet: boolean;
  children: JSX.Element;
}) => {
  if (!responsive.desktop && isResponsiveSheet) {
    return (
      <Button onPress={mobileSheet.handleOpen} disabled={isDisabled}>
        {children}
      </Button>
    );
  }

  return children;
};

const DropdownInput = ({
  // Shared props for both mobile and desktop
  name,

  value,
  onChangeValue,
  setFieldValue,
  onInputChange,
  isClearable,
  isDisabled,
  isLoading,
  isSearchable,
  isColorOptions,
  isResponsiveSheet,
  showDescriptionInOption,
  options,
  placeholder,

  // Mobile sheet only props
  label,

  // Customization props
  style,

  isSingleOptionSelected,
  additionalSearchKeys = [],

  // DropdownInput through props
  components,
  ...props
}: any) => {
  const mobileSheet = useSheet({name: 'Mobile Dropdown Sheet', enableTracking: false});
  const responsive = useResponsive();
  const isSectionedList = options[0]?.options;

  useMountEffect(() => {
    if (isSingleOptionSelected && _.size(options) === 1) {
      const option = options[0];
      const prevValue = null;
      const newValue = option ? option.value : null;
      setFieldValue(name, newValue);
      onChangeValue(newValue, option, prevValue);
    }
  });

  return (
    <React.Fragment>
      <SheetButtonWrapper
        responsive={responsive}
        mobileSheet={mobileSheet}
        isResponsiveSheet={isResponsiveSheet}
        isDisabled={isDisabled}
      >
        <BaseDropdownInput
          {...props}
          name={name}
          value={value}
          onChangeValue={onChangeValue}
          setFieldValue={setFieldValue}
          onInputChange={onInputChange}
          isClearable={isClearable}
          isLoading={isLoading}
          showDescriptionInOption={showDescriptionInOption}
          options={options}
          disabled={isDisabled}
          isOptionDisabled={(option: any) => option.isDisabled}
          // Customizations
          placeholder={isDisabled ? '' : placeholder}
          selectedOption={isSectionedList ? getSelectedSectionedOption({value, options}) : null}
          filterOption={getFilterOption({additionalSearchKeys})}
          fontSize={responsive.desktop ? 14 : 16}
          style={{
            height: responsive.desktop ? 36 : 48,
            ...style,
          }}
          DropdownIndicatorComponent={() => (
            <DropdownIndicatorComponent responsive={responsive} isDisabled={isDisabled} />
          )}
          optionStyle={isSectionedList ? {paddingLeft: 24} : null}
          menuIsOpen={responsive.desktop || !isResponsiveSheet ? undefined : false}
          isSearchable={isSearchable && responsive.desktop}
          components={components || getCustomComponents({isColorOptions})}
        />
      </SheetButtonWrapper>
      <DropdownSheet
        key={mobileSheet.key}
        isOpen={mobileSheet.isOpen}
        handleClose={mobileSheet.handleClose}
        headerText={label}
        options={options}
        name={name}
        value={value}
        onChangeValue={onChangeValue}
        setFieldValue={setFieldValue}
        onInputChange={onInputChange}
        isLoading={isLoading}
        isSearchable={isSearchable && getHasMoreThanFourOptions({options, isSectionedList})}
        isColorOptions={isColorOptions}
        showDescriptionInOption={showDescriptionInOption}
        additionalSearchKeys={additionalSearchKeys}
      />
    </React.Fragment>
  );
};

DropdownInput.createFilter = BaseDropdownInput.createFilter;
DropdownInput.OptionWithDescription = BaseDropdownInput.OptionWithDescription;
DropdownInput.OptionWithDescriptionOnRight = BaseDropdownInput.OptionWithDescriptionOnRight;
DropdownInput.OptionWithDescriptionAndSecondaryLabel =
  BaseDropdownInput.OptionWithDescriptionAndSecondaryLabel;
DropdownInput.OptionContainer = BaseDropdownInput.OptionContainer;
DropdownInput.SingleValueContainer = BaseDropdownInput.SingleValueContainer;
DropdownInput.Option = BaseDropdownInput.Option;
DropdownInput.OptionLabel = BaseDropdownInput.OptionLabel;
DropdownInput.OptionSecondaryLabel = BaseDropdownInput.OptionSecondaryLabel;

// Design system properties
DropdownInput.getSelectedSectionedOption = getSelectedSectionedOption;
DropdownInput.DropdownIndicatorComponent = DropdownIndicatorComponent;
DropdownInput.SheetButtonWrapper = SheetButtonWrapper;
DropdownInput.getFilterOption = getFilterOption;

// --------------------------------------------------
// Props
// --------------------------------------------------
DropdownInput.propTypes = {
  // Shared props for both mobile and desktop
  name: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  onChangeValue: PropTypes.func,
  setFieldValue: PropTypes.func,
  onInputChange: PropTypes.func,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isResponsiveSheet: PropTypes.bool,
  isSearchable: PropTypes.bool,
  showDescriptionInOption: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      handleAction: PropTypes.func,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          handleAction: PropTypes.func,
        }),
      ),
    }),
  ).isRequired,

  // Mobile sheet only props
  label: PropTypes.string,

  // Customization props
  isSingleOptionSelected: PropTypes.bool,
  style: PropTypes.object,
  additionalSearchKeys: PropTypes.arrayOf(PropTypes.string),
};

DropdownInput.defaultProps = {
  // Shared props for both mobile and desktop
  name: null,
  value: '',
  onChangeValue: () => {},
  setFieldValue: () => {},
  onInputChange: () => {},
  isClearable: false,
  isDisabled: false,
  isLoading: false,
  isResponsiveSheet: true,
  isSearchable: true,
  showDescriptionInOption: false,

  // Mobile sheet only props
  label: null,

  // Customization props
  isSingleOptionSelected: false,
  style: {},
  additionalSearchKeys: [],
};

export default DropdownInput;
